import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDateTimeIST } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell, CheckCircle2, Undo2, XCircle, Pencil } from "lucide-react";

const ICON = { approved: CheckCircle2, cr_accepted: Undo2, rejected: XCircle, cr_declined: XCircle, admin_edit: Pencil };
const TONE = { approved: "text-emerald-700", cr_accepted: "text-indigo-700", rejected: "text-rose-700", cr_declined: "text-rose-700", admin_edit: "text-amber-700" };

export function RosterUpdates() {
  const { actingOrgId } = useAuth();
  const qc = useQueryClient();
  const key = ["roster-updates", actingOrgId];
  const { data = [] } = useQuery({ queryKey: key, staleTime: 0, refetchOnMount: "always", queryFn: async () => (await api.get("/roster/updates")).data });
  if (!data.length) return null;
  const unread = data.filter((u) => !u.read).length;
  const markRead = async () => { await api.post("/roster/updates/read"); qc.invalidateQueries({ queryKey: key }); };
  return (
    <Card className="mb-5 p-5" data-testid="roster-updates">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-base font-bold text-slate-900"><Bell className="h-5 w-5 text-indigo-600" /> Roster updates
          {!!unread && <span className="rounded-full bg-indigo-600 px-2 py-0.5 text-xs text-white" data-testid="roster-updates-unread">{unread} new</span>}</p>
        {!!unread && <Button size="sm" variant="ghost" onClick={markRead} data-testid="roster-updates-read">Mark all as read</Button>}
      </div>
      <div className="max-h-72 space-y-2 overflow-y-auto">
        {data.map((u) => {
          const Icon = ICON[u.kind] || Bell;
          return (
            <div key={u.id} className={`flex gap-3 rounded-lg px-3 py-2.5 ${u.read ? "bg-slate-50" : "bg-indigo-50"}`} data-testid={`roster-update-${u.id}`}>
              <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${TONE[u.kind] || "text-slate-600"}`} />
              <div className="min-w-0"><p className="text-sm font-semibold text-slate-900">{u.title}</p>
                <p className="text-sm text-slate-700">{u.message}</p>
                <p className="text-xs text-slate-500">{formatDateTimeIST(u.created_at)}</p></div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
